import { array, nonEmptyArray, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { values } from '@mews/fp';
import { DistributorPromiseThunkAction } from '../../types';
import { getCityEnterprisesIds } from '../entities/enterprises/selectors';
import { getEnterpriseServiceId, getServiceEnterpriseId } from '../entities/services/selectors';
import {
    fetchResourceCategoriesFailed,
    fetchResourceCategoriesStarted,
    fetchResourceCategoriesSucceeded,
} from './reducer';
import { getCategories } from './selectors';

const fetchResourceCategories =
    (serviceId: string): DistributorPromiseThunkAction =>
    async (dispatch, getState, { repositories }) => {
        dispatch(fetchResourceCategoriesStarted({ serviceId }));

        const enterpriseId = getServiceEnterpriseId(getState(), { serviceId });

        try {
            if (enterpriseId == null) {
                throw new Error('Cannot fetch resource categories without enterpriseId');
            }

            const response = await repositories.resourceCategories.getAll({
                serviceId,
                extent: { imageAssignments: true },
            });

            dispatch(fetchResourceCategoriesSucceeded({ ...response, enterpriseId }));
        } catch (error) {
            dispatch(fetchResourceCategoriesFailed({ serviceId }));
            throw error;
        }
    };

const fetchCityResourceCategories =
    (cityId: string): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const enterpriseIds = getCityEnterprisesIds(state, { cityId });

        const promises = pipe(
            enterpriseIds,
            array.map((enterpriseId) => option.fromNullable(getEnterpriseServiceId(state, { enterpriseId }))),
            array.compact,
            array.map(async (serviceId) => dispatch(fetchResourceCategories(serviceId)))
        );

        await Promise.all(promises);
    };

const shouldFetchCityResourceCategories = createSelector(
    getCityEnterprisesIds,
    getCategories,
    (cityEnterprisesIds, categories) => {
        if (record.isEmpty(categories)) {
            return true;
        }

        const categoriesRecord = pipe(
            categories,
            values,
            nonEmptyArray.groupBy((category) => category.enterpriseId)
        );

        return pipe(
            cityEnterprisesIds,
            array.map((id) => record.lookup(id, categoriesRecord)),
            array.sequence(option.Applicative),
            option.isNone
        );
    }
);

export const fetchCityResourceCategoriesWithCheck =
    (cityId: string): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        if (shouldFetchCityResourceCategories(state, { cityId })) {
            await dispatch(fetchCityResourceCategories(cityId));
        }
    };
